import { useEffect, useMemo, useState } from 'react'

import debounce from 'lodash.debounce'

import axios from 'axios'

// Query-scoped suggestions for the nav search dropdown, split by product type. Mirrors the
// homepage hook (NewHomepage/Components/Search/useGroupedLiveSearch) but with plain axios:
// the nav renders on every page, so it must not pull the react-query client into its bundle.
// Response shape: { data: { cg: [[phrase, { thumbnailUrl, itemTitle, total }]], print_ready: [...] } }.
// The previous query's results are kept while the next request is in flight (and on error), so
// the dropdown never flashes the curated columns between keystrokes. `results` is null until a
// non-empty query resolves, letting the dropdown fall back to its curated columns. `isLoading`
// covers the 300ms debounce window as well as the request itself.
function useGroupedLiveSearch({ query = '', enabled = false } = {}) {
  const trimmedQuery = query.trim()
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery)
  const [data, setData] = useState(null)
  const [isFetching, setIsFetching] = useState(false)

  const debouncedSetter = useMemo(() => debounce(setDebouncedQuery, 300), [])

  useEffect(() => {
    if (!trimmedQuery) {
      debouncedSetter.cancel()
      setDebouncedQuery('')
      return undefined
    }

    debouncedSetter(trimmedQuery)

    return () => debouncedSetter.cancel()
  }, [trimmedQuery, debouncedSetter])

  useEffect(() => {
    if (!enabled || !debouncedQuery) {
      setIsFetching(false)
      return undefined
    }

    const controller = new AbortController()
    setIsFetching(true)

    axios
      .get('/api/internal/items/grouped_live_search', {
        params: { query: debouncedQuery },
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal,
        timeout: 3000,
      })
      .then((response) => {
        setData(response.data?.data || null)
      })
      .catch(() => {
        // Keep the previous results on error (mirrors react-query's behavior on the homepage);
        // the dropdown shows its curated columns when nothing has resolved yet.
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsFetching(false)
        }
      })

    return () => controller.abort()
  }, [enabled, debouncedQuery])

  const results = trimmedQuery.length > 0 ? data : null

  const isDebouncePending = trimmedQuery !== debouncedQuery
  const isLoading = enabled && trimmedQuery.length > 0 && (isDebouncePending || isFetching)

  return { results, isLoading }
}

export { useGroupedLiveSearch }
